/** UI state of the template module: the "use template" and "settings" dialogs (ADR 0024). */
import type { Editor } from '@tiptap/core';
import { create } from 'zustand';

export interface TemplateUse {
  /** null: pick a template first. */
  templateId: string | null;
  /** 'page': make a new page; 'insert': put the text at the cursor of `editor`. */
  mode: 'page' | 'insert';
  /** Overrides the template's target (a database's "New" button). */
  targetId?: string | null;
  editor?: Editor;
}

interface TemplateUi {
  use: TemplateUse | null;
  settingsFor: string | null;
  /** A page just made from a template: its {{cursor}} becomes the cursor. */
  cursorPage: string | null;
  set: (patch: Partial<Omit<TemplateUi, 'set'>>) => void;
}

export const useTemplateUi = create<TemplateUi>((set) => ({
  use: null,
  settingsFor: null,
  cursorPage: null,
  set: (patch) => set(patch),
}));

export const openUseTemplate = (use: TemplateUse) => useTemplateUi.getState().set({ use });

/** Put the cursor where the template had {{cursor}} (and remove the marker). */
export function placeCursorMarker(editor: Editor): boolean {
  const marker = '{{cursor}}';
  let found: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (found !== null) return false;
    if (node.isText) {
      const i = node.text!.indexOf(marker);
      if (i >= 0) found = pos + i;
    }
    return true;
  });
  if (found === null) return false;
  editor
    .chain()
    .focus()
    .deleteRange({ from: found, to: found + marker.length })
    .setTextSelection(found)
    .run();
  return true;
}
